/*
 * Copyright Innov'ATM all rights reserved.
 * This software is the property of Innov'ATM and may not be used in any manner except under a
 * license agreement signed with Innov'ATM.
 */

import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';

import { globalAxios } from '../backend/utils';
import { useEvent } from './useEvent';

interface UseRegisterRenewTokenInterceptorParams {
    renewTokenHttpCode: number;
    isUserLoggedIn: boolean;
    renewTokens?: () => Promise<string | null | undefined>;
    logout?: () => Promise<void>;
}

const MIN_RENEW_DELAY = 1000 * 60; // 1 minute
let lastRenew: number | null = null;

export function useRegisterRenewTokenInterceptor({
    renewTokenHttpCode,
    isUserLoggedIn,
    renewTokens,
    logout,
}: UseRegisterRenewTokenInterceptorParams): {
    isRenewTokenInterceptorRegistered: boolean;
} {
    const [isRenewTokenInterceptorRegistered, setIsRenewTokenInterceptorRegistered] = useState(false);

    const onError = useEvent(async function (error: AxiosError) {
        const originalRequest = error?.config;

        if (
            error?.response?.status === renewTokenHttpCode &&
            !!originalRequest?.headers?.Authorization &&
            !(originalRequest as any)?._retry &&
            isUserLoggedIn
        ) {
            if (lastRenew && Date.now() - lastRenew < MIN_RENEW_DELAY) {
                throw error;
            }

            // tag the request as retrying (to avoid trying multiple times)
            (originalRequest as any)._retry = true;

            let newToken: string | null | undefined = undefined;
            try {
                newToken = await renewTokens?.();
                lastRenew = Date.now();
            } catch (renewError) {
                await logout?.();
                throw error;
            }
            if (!newToken) {
                await logout?.();
                throw error;
            }

            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            // Retry the request with the new token
            return globalAxios(originalRequest);
        }

        throw error;
    });

    useEffect(() => {
        const id = globalAxios.interceptors.response.use((response: any) => response, onError);

        setIsRenewTokenInterceptorRegistered(true);

        return () => {
            globalAxios.interceptors.response.eject(id);
        };
    }, [onError]);

    return { isRenewTokenInterceptorRegistered };
}
